import CardsContext from '@/context/CardsContext';
import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
export interface CardMedia {
	// ID of the file
	id: string;
	// Date and time the photo was taken
	timestamp: string;
	// Specific species name from the photo
	identifiedAs: string;
	// Store how the photo was identified (automatically or manually)
	identifiedBy?: 'automatically' | 'manually';
	// Mime type
	mime: 'image/jpg' | 'audio/wav' | 'audio/mp4' | string;
}

export type CardMediaType = 'photo' | 'audio';

export type CardMediaWithType = CardMedia & { type: CardMediaType };

export interface Card {
	// Unique card ID
	id: string;
	// Species id
	speciesId: number;
	// Photos stored in this card
	photos: CardMedia[];
	// Audio recordings stored in this card
	audio?: CardMedia[];
}

const useCards = () => useContext(CardsContext);

export const useCardMainMedia = (
	speciesId: number,
	types: CardMediaType[],
	asThumbnail: boolean = false,
	isInView: boolean = true,
	tryIndex: number = 0,
) => {
	const params = useParams();
	const { getCardBySpeciesId, readPhoto, readAudio } = useCards();

	const [isUserPhotoAvailable, setIsUserPhotoAvailable] =
		useState<boolean>(false);
	const [mainUserPhoto, setMainUserPhoto] = useState<
		string | null | undefined
	>(undefined);

	const [isUserAudioAvailable, setIsUserAudioAvailable] =
		useState<boolean>(false);

	useEffect(() => {
		if (!isInView) return;

		const card = getCardBySpeciesId(speciesId);
		// Load photo if it exists
		if (types.includes('photo') && card && card.photos.length > 0) {
			setIsUserPhotoAvailable(true);
			readPhoto(card.photos[tryIndex ? tryIndex % card.photos.length : 0].id, asThumbnail).then(setMainUserPhoto);
		} else {
			setMainUserPhoto(null);
		}
		// Load audio if it exists
		if (
			types.includes('audio') &&
			card &&
			card.audio &&
			card.audio.length > 0
		) {
			setIsUserAudioAvailable(true);
		} else {
			setIsUserAudioAvailable(false);
		}
	}, [params, isInView, getCardBySpeciesId, readPhoto, speciesId, mainUserPhoto, asThumbnail, types, readAudio, tryIndex]);

	return {
		isUserPhotoAvailable,
		mainUserPhoto,
		isUserAudioAvailable,
	};
};

export const getAllCardMediaWithTypes = (card: Card): CardMediaWithType[] => {
	const allMediaWithTypes = [
		...card.photos.map(
			(photo: CardMedia): CardMediaWithType => ({
				...photo,
				type: 'photo',
			})
		),
		...(card.audio ?? []).map(
			(audio: CardMedia): CardMediaWithType => ({
				...audio,
				type: 'audio',
			})
		),
	];
	// Sort them by timestamp string, newest first
	allMediaWithTypes.sort((a, b) => {
		if (a.timestamp < b.timestamp) return 1;
		if (a.timestamp > b.timestamp) return -1;
		return 0;
	});
	return allMediaWithTypes;
};

export default useCards;
